import { useMemo } from 'react';
import { ProductType } from '../types';
import {
  getTooltipText,
  getSavingsText,
  calculateTotalSavings,
  getBonusCountText,
} from '../config/savingsConfig';
import { getBenefitShortName } from '../config/slidesConfig';

interface UseSavingsCalculatorReturn {
  tooltipText: string;
  getBenefitText: (slideId: string) => string | null;
  getTotalSavings: (slideId: string) => string;
  getNextButtonText: (currentSlideId: string) => string;
  getBonusCountText: (slideId: string) => string;
}

/**
 * Benefici dinamici da database CAP (come slider_all_bonus_short_cap).
 */
export function useSavingsCalculator(
  productType?: ProductType,
  zipFlag?: boolean | null,
  zipCode?: string
): UseSavingsCalculatorReturn {
  const tooltipText = useMemo(() => {
    return getTooltipText(productType);
  }, [productType]);

  const getBenefitText = useMemo(() => {
    return (slideId: string) => getSavingsText(slideId, productType, zipFlag, zipCode);
  }, [productType, zipFlag, zipCode]);

  const getTotalSavings = useMemo(() => {
    return (slideId: string) => calculateTotalSavings(slideId, productType, zipFlag, zipCode);
  }, [productType, zipFlag, zipCode]);

  const getNextButtonText = useMemo(() => {
    return (currentSlideId: string) => {
      const buttonMapping: Record<string, string> = {
        email_form: 'name_form',
        name_form: 'phone_form',
      };

      const nextSlideId = buttonMapping[currentSlideId];
      if (!nextSlideId) return 'Avanti';

      const nextBenefitText = getSavingsText(nextSlideId, productType, zipFlag, zipCode);
      const shortName = getBenefitShortName(nextBenefitText);
      return `Scopri ${shortName}`;
    };
  }, [productType, zipFlag, zipCode]);

  const getBonusCountTextMemo = useMemo(() => {
    return (slideId: string) => getBonusCountText(slideId, productType, zipCode);
  }, [productType, zipCode]);

  return {
    tooltipText,
    getBenefitText,
    getTotalSavings,
    getNextButtonText,
    getBonusCountText: getBonusCountTextMemo,
  };
}
